<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useWindowSize } from '@vueuse/core';
import { IRouteMeta } from '../models/routing';
import { AppCoreEnums } from '../shared/enums';
import { useRoute, useRouter } from 'vue-router';
import { useCoreStore } from '../../stores/core/store';
import { PropType, computed, ref, watchEffect } from 'vue';
import AppDivider from '@/core/components/app-divider.vue';
import ContainerTable from '../components/container-table.vue';
import useRouteUtils from '../../core/router-utils/route-utils';
import ContainerToolbar from '../toolbars/container-toolbar.vue';
import appPaginator from '../components/common/app-paginator.vue';
import { TableBasedContainerOptions } from '../../stores/core/models';
import AppSearchInput from '@/core/components/form/app-search-input.vue';
import AppDrawerTeleport from '../../core/drawers/app-drawer-teleport.vue';
import AppCollapsibleButton from '../components/common/app-collapsible-button.vue';
import useTableBasedContainer from '../composable/containers/use-table-based-container';
import TableBasedContainerFilters from '../components/table-based-container-filters.vue';
import useMainMenuItems from '@/composables/use-main-menu-items';

const emit = defineEmits(['new']);

const props = defineProps({
  options: {
    required: true,
    type: Object as PropType<TableBasedContainerOptions>,
  },
  alwaysHideLeftPanel: {
    type: Boolean,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const leftPanelRef = ref();
const coreStore = useCoreStore();
const { getMenuItemLabel } = useMainMenuItems();
const { getContainerNewRouteName } = useRouteUtils();
const pagination = computed(() => containerData.value.pagination);
const { height: windowHeight, width: windowWidth } = useWindowSize();
const { collapsibleTableContainerData: containerData, sizes, isCollapsibleTableContainerMinimized } = storeToRefs(coreStore);

const { onPage, onSort, onRefresh, onDelete, onSearchChanged } = useTableBasedContainer(
  pagination,
  AppCoreEnums.ContainerType.CollapsibleTableContainer
);

const onNew = () => {
  const newRouteName = getContainerNewRouteName(AppCoreEnums.ContainerType.EmptyContainer);
  if (newRouteName) {
    router.push({ name: newRouteName });
  } else {
    emit('new');
  }
};

const toggleLeftPanelMiniMode = (useMiniMode: boolean) => {
  coreStore.setIsCollapsibleTableContainerLeftPanelInMiniMode(useMiniMode);
};

const scrollHeight = computed(() => {
  return (
    windowHeight.value -
    sizes.value.breadcrumbsHeight -
    40 - // <- for title
    125 -
    50 - // <- for paginator
    (containerData.value.isLeftPanelCollapsed || containerData.value.isLeftPanelInMiniMode ? 120 : 0) -
    sizes.value.collapsibleTableContainerToolbarHeight -
    sizes.value.collapsibleTableContainerFiltersHeight
    //(sizes.value.collapsibleTableContainerFiltersHeight == 0 ? 108 : 108)
  );
});

const scrollwidth = computed(() => {
  return windowWidth.value - sizes.value.mainMenuWidth - 90;
});

const leftPanelWidth = computed(() => {
  if (containerData.value.isLeftPanelInMiniMode) return 50;
  if (containerData.value.isLeftPanelCollapsed) return props.alwaysHideLeftPanel ? 0 : 295;
  return 0;
});

const leftPanelStyle = computed(() => {
  return {
    minWidth:
      containerData.value.options.useMaximizedCustomLeftPanel() &&
      containerData.value.options.useCustomLeftPanel() &&
      !containerData.value.isLeftPanelInMiniMode
        ? '60%'
        : leftPanelWidth.value || props.alwaysHideLeftPanel
        ? `${leftPanelWidth.value}px`
        : '100%',
    maxWidth: leftPanelWidth.value
      ? `${leftPanelWidth.value}px`
      : props.alwaysHideLeftPanel && containerData.value.isLeftPanelCollapsed
      ? '0%'
      : '100%',
  };
});

const rightPanelStyle = computed(() => {
  return {
    minWidth: `calc(100% - ${leftPanelWidth.value + 48}px)`,
  };
});
watchEffect(() => (containerData.value.options = props.options));
watchEffect(() => (containerData.value.title = props.options.getTitle()));
watchEffect(() => (sizes.value.collapsibleTableContainerLeftPanelWidth = leftPanelWidth.value));
//watchEffect(() => (sizes.value.collapsibleTableContainerLeftPanelWidth = leftPanelElementWidth.value));
watchEffect(
  () =>
    (containerData.value.options.toolbarOptions.isDeleteEnabled =
      containerData.value.options.toolbarOptions.canDelete && containerData.value.selectedRows.length > 0)
);
watchEffect(() => {
  let title = coreStore.getTableBasedContainerSelectedRowTitle('id', AppCoreEnums.ContainerType.CollapsibleTableContainer);
  title = title || containerData.value.detailsTitle;

  if (title && (route.meta as IRouteMeta).translateCollapsibleTableTitleField) containerData.value.detailsTitle = t(title);
  else containerData.value.detailsTitle = title;
});
</script>

<template>
  <div v-if="isCollapsibleTableContainerMinimized">
    <router-view></router-view>
  </div>

  <div
    v-show="!isCollapsibleTableContainerMinimized"
    class="flex flex-col w-full overflow-hidden pb-3"
    style="height: calc(100vh - 71px)"
  >
    <!-----------
    -- Toolbar --
    ------------->
    <container-toolbar
      :title="getMenuItemLabel(AppCoreEnums.ContainerType.CollapsibleTableContainer) || options.getTitle()"
      :badge="pagination.rowsNumber"
      :options="containerData.options.toolbarOptions"
      @new="onNew"
      @refresh="onRefresh"
      @delete="onDelete"
      :hide-all="containerData.isLeftPanelCollapsed"
    />

    <!--
    <collapsible-container-toolbar-label
      v-model="containerData.showFilters"
      :title="options.getTitle()"
      :options="containerData.options.toolbarOptions"
      :hide-all="containerData.isLeftPanelCollapsed"
    />

    <collapsible-container-toolbar-items
      v-model="containerData.showFilters"
      :options="containerData.options.toolbarOptions"
      :hide-all="containerData.isLeftPanelCollapsed"
      @new="onNew"
      @refresh="onRefresh"
      @delete="onDelete"
    /-->

    <!-----------
    -- Filters -- 
    ------------->
    <table-based-container-filters
      class="mt-6"
      @search-changed="onSearchChanged"
      :containerType="AppCoreEnums.ContainerType.CollapsibleTableContainer"
      v-show="!containerData.isLeftPanelCollapsed"
      v-if="containerData.showFilters"
    >
      <template #filters>
        <slot name="filters"></slot>
      </template>
      <template #filters-right>
        <slot name="filters-right"></slot>
      </template>
    </table-based-container-filters>

    <Splitter
      class="w-full flex grow border-0"
      :gutter-size="alwaysHideLeftPanel ? 0 : 1"
      :ptOptions="{ mergeSections: true, mergeProps: true }"
      :pt="{
        gutterhandler: '!h-full dark:!bg-app-separator ',
      }"
    >
      <!--------------
      -- Left Panel -- 
      ---------------->
      <SplitterPanel
        :ptOptions="{ mergeSections: true, mergeProps: true }"
        :style="leftPanelStyle"
        :class="{ '!mr-7 ': containerData.isLeftPanelCollapsed && !props.alwaysHideLeftPanel }"
        ref="leftPanelRef"
      >
        <template v-if="containerData.isLeftPanelCollapsed">
          <div class="flex justify-between items-center">
            <div
              class="flex items-center w-full"
              :class="{
                'justify-between': !containerData.isLeftPanelInMiniMode && options.useCustomLeftPanel(),
                'justify-end': containerData.isLeftPanelInMiniMode || !options.useCustomLeftPanel(),
              }"
            >
              <div
                class="dark:border-r-surface-700 font-semibold text-gray-900 dark:text-gray-100"
                v-if="!containerData.isLeftPanelInMiniMode && options.useCustomLeftPanel()"
              >
                <span class="font-semibold text-[21px]">{{ options.customLeftPanelTitle }}</span>
              </div>
              <app-collapsible-button
                @toggle="toggleLeftPanelMiniMode"
                :isCollapsed="containerData.isLeftPanelInMiniMode"
                :isVisible="containerData.isLeftPanelCollapsed"
                class="ml-auto"
              />
            </div>
          </div>
          <div
            class="mt-4"
            v-show="!options.useCustomLeftPanel() && (!containerData.isLeftPanelCollapsed || !props.alwaysHideLeftPanel)"
          >
            <!----------
            -- Search --
            ------------>
            <app-search-input
              :width="295"
              :container-type="AppCoreEnums.ContainerType.CollapsibleTableContainer"
              @search-changed="onSearchChanged"
              v-if="!containerData.isLeftPanelInMiniMode"
            />

            <app-divider class="!mt-2 pb-5" v-if="!containerData.isLeftPanelInMiniMode" />
          </div>
        </template>

        <section v-show="!options.useCustomLeftPanel()">
          <div
            class="mt-4"
            v-show="!containerData.isLeftPanelInMiniMode && (!containerData.isLeftPanelCollapsed || !alwaysHideLeftPanel)"
          >
            <!---------
            -- Table -- 
            ----------->
            <container-table
              @sort="onSort"
              :scrollHeight="scrollHeight"
              :scrollwidth="scrollwidth"
              v-model:containerData="containerData"
              :show-only-first-column="containerData.isLeftPanelCollapsed"
              :use-last-tabs-container-route-for-details="containerData.isLeftPanelCollapsed"
              @after-details-navigation="coreStore.setIsCollapsibleTableContainerLeftPanelCollapsed(true)"
            >
              <template #actions="{ row }">
                <slot name="actions" :row="row"></slot>
              </template>

              <template #custom="{ row, rowData, col }">
                <slot name="custom" :row="row" :rowData="rowData" :col="col"></slot>
              </template>

              <template #custom2="{ row }">
                <slot name="custom2" :row="row"></slot>
              </template>

              <template #custom3="{ row }">
                <slot name="custom3" :row="row"></slot>
              </template>
            </container-table>
          </div>

          <app-divider
            class="pt-3 !mb-1"
            v-if="!containerData.isLeftPanelInMiniMode && (!containerData.isLeftPanelCollapsed || !alwaysHideLeftPanel)"
          />
          <!-------------
          -- Paginator -- 
          --------------->
          <!--app-paginator
            @page="onPage"
            :pagination="containerData.pagination"
            v-show="!containerData.isLeftPanelInMiniMode && (!containerData.isLeftPanelCollapsed || !alwaysHideLeftPanel)"
            :include-rows-per-page="!containerData.isLeftPanelCollapsed"
          /-->

          <app-paginator
            @page="onPage"
            :pagination="containerData.pagination"
            v-show="!containerData.isLeftPanelInMiniMode && (!containerData.isLeftPanelCollapsed || !alwaysHideLeftPanel)"
            :include-rows-per-page="!containerData.isLeftPanelCollapsed"
          />

          <slot name="bottom" />
        </section>
        <section v-show="!containerData.isLeftPanelInMiniMode && options.useCustomLeftPanel()">
          <slot name="customLeft"></slot>
        </section>
      </SplitterPanel>

      <!------------------------
      -- Content(Right) Panel --
      -------------------------->
      <SplitterPanel
        class="flex align-items-center justify-content-center h-full pr-[1px]"
        :class="{ 'pl-5': !alwaysHideLeftPanel }"
        v-if="containerData.isLeftPanelCollapsed"
        :style="rightPanelStyle"
      >
        <!--------------
        -- Child Root -- 
        ---------------->
        <div class="w-full">
          <router-view />
        </div>
      </SplitterPanel>
    </Splitter>
  </div>

  <!-------------------->
  <!-- Drawer Content -->
  <!-------------------->
  <app-drawer-teleport>
    <template #default="{ id }">
      <slot name="drawers" v-bind:id="id"></slot>
    </template>
  </app-drawer-teleport>
</template>
